/* dd-marquee.js
 * ─────────────
 * Compositor-driven marquee with scroll-modulated playback rate.
 *
 * Why this exists / what changed from the inline implementation:
 *
 *   The original `<script>` block in sections/dd-marquee.liquid drove the
 *   whole animation from a main-thread requestAnimationFrame loop:
 *
 *     1. scroll listener accumulates `scrollBoost`
 *     2. rAF reads scrollBoost, computes new pos, writes
 *        `track.style.transform = 'translateX(...)'`
 *
 *   On desktop the main thread has plenty of headroom and this looks
 *   fine. On mobile it doesn't:
 *
 *     • Mobile browsers scroll on the compositor thread independently
 *       of JS. Main-thread transform animations compete with scroll
 *       handling, paint, layout, GC, and any other JS for frame slots.
 *       Mid-tier Android in particular regularly blocks the main
 *       thread for 16–32ms, which the user perceives as marquee
 *       stutter — and worst right when scroll-driven speed changes
 *       happen, because that's when the main thread is busiest.
 *     • Scroll events fire in unpredictable bursts on mobile (coalesced
 *       by the browser). A single fling event can carry dy=80px and
 *       dump a huge spike into scrollBoost, which then has to decay
 *       across many frames. Any frame-drop during the decay is visible.
 *     • `translateX(...px)` written as a fresh string every frame is
 *       parsed by the browser each time, and on older mobile Safari
 *       isn't always promoted to its own compositor layer even with
 *       `will-change: transform`.
 *     • The original loop ran forever even when the marquee was
 *       scrolled offscreen — pure battery/CPU waste.
 *
 *   The Web Animations API runs transform-only animations on the
 *   compositor thread. Once `track.animate()` is started, the loop
 *   continues even when the main thread is blocked — exactly the
 *   property mobile needs. We then modulate `animation.playbackRate`
 *   from a rAF loop to produce the scroll-boost effect; that's a
 *   single numeric write per frame, no string parsing, no transform
 *   matrix recomputation.
 *
 * Architecture:
 *
 *   1. WAAPI animation: keyframes translate3d(0,0,0) → translate3d(-50%,0,0),
 *      iterations:Infinity, easing:linear. Runs on the compositor.
 *      The track markup duplicates the block list 8× so half-points
 *      are identical content and -50% loops seamlessly.
 *   2. rAF loop reads window.scrollY ONCE per frame (pull-based) and
 *      computes a delta. No scroll listener — this avoids main-thread
 *      contention with the compositor's scroll thread and also
 *      naturally handles the coalesced-event problem.
 *   3. scrollBoost (per-frame) is added to a running velocity, with
 *      friction applied every frame so it decays naturally back to
 *      base speed. Boost is mapped to a playbackRate multiplier
 *      around 1.0 (base), clamped to a sensible range that allows
 *      direction reversal (matching the original visual behaviour).
 *   4. IntersectionObserver pauses the WAAPI animation AND the rAF
 *      loop when the marquee is offscreen. Negligible cost while
 *      offscreen, instant resume when scrolled back into view.
 *
 * Multi-instance safety:
 *   The original inline script used `window._ddMarqueeParallax` as a
 *   one-shot init guard — if a page rendered two `.dd-marquee`
 *   sections, only the first would animate. We init per element using
 *   a `data-dd-marquee-init` flag on the track, so every instance gets
 *   its own animation and IntersectionObserver.
 */

(function () {
  'use strict';

  /* Honour user-level animation preferences. The original implementation
     also bailed early on prefers-reduced-motion; we keep that contract
     so users who set the OS-level toggle never see the marquee move. */
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  /* WAAPI feature-detect. element.animate is present in every browser
     we care about (Chrome 36+, Safari 13.1+, Firefox 48+, all modern
     mobile browsers including iOS Safari 14+), but a guard costs
     nothing and lets the section degrade to a static track on the
     long tail rather than throwing. */
  var hasWAAPI =
    typeof Element !== 'undefined' &&
    typeof Element.prototype.animate === 'function';

  /* Tuning constants — tuned to roughly match the visual feel of the
     original BASE = (speed * 0.07) px/frame implementation while
     allowing reversed direction on hard upward scroll. Adjust if the
     scroll-boost feels too snappy or too sluggish.

       SCROLL_SENSITIVITY  — how much each px of page scroll
                             contributes to playbackRate per frame.
       FRICTION            — per-frame decay applied to scrollBoost
                             (0.9 = ~10% gone per frame, ~63% gone in
                             10 frames).
       MIN_RATE / MAX_RATE — playbackRate clamp. Negative values
                             reverse direction, matching the original
                             schema description ("scrolling the page
                             temporarily speeds up or reverses").
       PX_PER_SEC_PER_SPEED — pixels per second that the track moves
                             at playbackRate=1 per unit of `speed`.
                             Matches the original implementation:
                             BASE was `speed * 0.07` px/frame, which
                             at 60fps is `speed * 4.2` px/sec. So at
                             the default speed=10 the track travels
                             42 px/sec — slow enough to read.

     IMPORTANT: duration of the WAAPI animation must be computed from
     the track's actual measured pixel width, not from a fixed ms
     constant. The keyframes translate by 50% of the track's *own*
     width (`-25%` → `-75%`), and the markup duplicates the block list
     8× — so 50% of trackWidth is many viewports of pixels. A fixed
     ms duration like the previous BASE_DURATION_MS=70000 produced an
     effective velocity around 1000 px/sec on mobile (24× faster than
     the original's 42 px/sec), which is why the marquee was unreadable.
     We measure scrollWidth at animation start and derive duration so
     the on-screen velocity matches the legacy implementation regardless
     of how wide the duplicated track ends up. */
  var SCROLL_SENSITIVITY = 0.012;
  var FRICTION = 0.9;
  var MIN_RATE = -4;
  var MAX_RATE = 12;
  var PX_PER_SEC_PER_SPEED = 4.2;

  /* Detect mobile-class viewports once at init. Same breakpoint the
     rest of the theme uses for "mobile vs desktop" structural choices
     (see assets/dd-glass-nav.css ~line 1259, dd-pdp-mobile.css, etc.).

     On mobile we skip the parallax (scroll-driven playback-rate
     modulation) entirely. The WAAPI animation still runs — it's
     compositor-driven so it's effectively free — but we don't spin up
     the per-frame requestAnimationFrame loop that samples scrollY and
     writes animation.playbackRate, and we don't react to scroll at
     all. Reasoning:

       1. Even though our rAF loop only does one numeric write per
          frame (the playbackRate update), changing playbackRate on a
          compositor-promoted WAAPI animation forces the compositor to
          recompute its timing each time. Doing that 60× a second
          while the user is mid-scroll means the compositor is
          re-syncing the marquee's clock at exactly the moment it's
          also trying to scroll the page — and on mid-tier mobile
          GPUs that contention shows up as visible jitter even though
          the animation itself is "off the main thread".
       2. The parallax effect's value is mostly desktop polish — a
          gentle speed-up as you scroll past the section. On a phone
          screen the section is taller relative to viewport and
          scrolls faster anyway, so the boost is barely perceptible
          even when working perfectly. Better to ship a rock-solid
          constant marquee than a "parallax" that visibly stutters.
       3. With this branch, mobile is essentially equivalent to a
          pure CSS @keyframes animation in terms of work performed —
          which is the smoothest implementation that exists. */
  var isMobileViewport =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 1024.98px)').matches;

  /* DIAGNOSTIC GATE — temporary.
     The desktop parallax loop is suspected of causing a "frozen on
     scroll-down" failure where the compositor commit lag from the
     pause→play→playbackRate-write sequence intersects with a stale
     scrollBoost value, leaving the animation effectively pinned.
     We gate the entire scroll-driven rate modulation behind a query
     param so the default behavior is the simplest possible: WAAPI
     animation runs at constant playbackRate=1, identical to a pure
     CSS @keyframes loop. This isolates the marquee's correctness
     from the parallax feature. To re-enable parallax for testing,
     append `?marqueeParallax=1` to any URL. Once we've confirmed
     whether parallax is in fact the culprit, this gate either
     becomes the new default (parallax retired) or the structural
     fix lands and the gate is removed. */
  var parallaxEnabled = false;
  try {
    var urlParams = new URLSearchParams(window.location.search);
    parallaxEnabled = urlParams.get('marqueeParallax') === '1';
  } catch (e) {
    /* Old browsers without URLSearchParams — leave parallax off. */
  }

  function initMarquee(marquee) {
    var track = marquee.querySelector('.dd-marquee__track');
    if (!track) return;
    if (track.getAttribute('data-dd-marquee-init') === '1') return;
    track.setAttribute('data-dd-marquee-init', '1');

    var speed = parseFloat(marquee.getAttribute('data-parallax-speed'));
    if (!isFinite(speed)) speed = 10;

    /* Speed = 0 → static. Don't even spin up an animation, just leave
       the track at its default transform. */
    if (speed <= 0) return;

    var animation = null;
    var visible = false;
    var rafId = null;
    var scrollBoost = 0;
    var lastScrollY = 0;
    /* Retry budget for ensureAnimation()'s scrollWidth=0 case (see
       below). Bounded so a permanently zero-width track (display:none
       parent, etc.) can't pin a rAF loop forever. ~2s at 60fps is
       longer than any realistic layout/font/lazy-image settle window
       on this section. */
    var widthRetryBudget = 120;
    var widthRetryRafId = null;

    function ensureAnimation() {
      if (animation) return;
      if (!hasWAAPI) return;

      /* Measure the track's rendered width to derive an animation
         duration that produces the same on-screen velocity as the
         legacy implementation. We need real pixels here because the
         keyframes use percentages relative to the (very wide,
         8×-duplicated) track.

         If layout hasn't fully settled at IO-callback time
         (font swap mid-flight, images mid-decode, the section just
         being rendered live by the theme editor) scrollWidth can come
         back as 0. Previously we early-returned and relied on the IO
         callback firing again — but IO only re-fires on intersection
         *change*, not on layout settle, so a single transient
         zero-width measurement could strand the marquee static until
         the user scrolled out and back in. We instead schedule a rAF
         retry; on the next frame layout has typically completed and
         the second measurement succeeds. The retry budget caps the
         loop so a genuinely zero-sized track (e.g. parent
         display:none) doesn't burn a rAF slot forever. */
      var trackWidthPx = track.scrollWidth;
      if (trackWidthPx <= 0) {
        if (widthRetryBudget > 0) {
          widthRetryBudget--;
          widthRetryRafId = requestAnimationFrame(ensureAnimation);
        }
        return;
      }
      /* Cancel any pending retry — we got a real measurement, no need
         to keep polling. */
      if (widthRetryRafId !== null) {
        cancelAnimationFrame(widthRetryRafId);
        widthRetryRafId = null;
      }

      /* The keyframe span is -25% → -75% (50% of trackWidth) so the
         track translates by halfWidthPx pixels over `duration` ms. */
      var halfWidthPx = trackWidthPx / 2;
      var pxPerSecond = speed * PX_PER_SEC_PER_SPEED;
      var duration = (halfWidthPx / pxPerSecond) * 1000;

      /* Keyframes start at -25% to match the CSS default in
         dd-marquee.css. Without this alignment WAAPI would snap from
         the CSS-applied -25% to 0% in the first animation frame, which
         is visible as a small jump at handoff. With content duplicated
         8× in the markup, -25% and -75% are visually identical
         positions, so animating between them produces the same
         seamless loop as 0% → -50% would. */
      animation = track.animate(
        [
          { transform: 'translate3d(-25%, 0, 0)' },
          { transform: 'translate3d(-75%, 0, 0)' }
        ],
        {
          duration: duration,
          iterations: Infinity,
          easing: 'linear'
        }
      );

      /* If the section is currently offscreen at the moment the
         animation is created (which happens whenever eager init's
         rAF-retry path resolves AFTER IO has already fired its
         initial isIntersecting:false callback), pause immediately so
         the compositor doesn't burn cycles on something the user
         can't see. The IO will resume it when the section reaches
         the viewport. Without this guard the animation would run
         24/7 between init and first scroll-into-view. */
      if (!visible) {
        animation.pause();
      }
    }

    function loop() {
      rafId = null;

      var currentScrollY = window.pageYOffset || 0;
      var dy = currentScrollY - lastScrollY;
      lastScrollY = currentScrollY;

      /* Apply scroll delta then decay. Doing both in the same frame
         keeps the boost responsive (immediate kick on a fast scroll)
         while ensuring it drifts back to base speed when the user
         stops moving. */
      scrollBoost += dy * SCROLL_SENSITIVITY;
      scrollBoost *= FRICTION;

      var rate = 1 + scrollBoost;
      if (rate < MIN_RATE) rate = MIN_RATE;
      if (rate > MAX_RATE) rate = MAX_RATE;

      if (animation) {
        animation.playbackRate = rate;
      }

      /* Only keep looping while visible. IntersectionObserver flips
         `visible` and triggers/halts the loop accordingly. */
      if (visible) {
        rafId = requestAnimationFrame(loop);
      }
    }

    function startLoop() {
      if (rafId !== null) return;
      lastScrollY = window.pageYOffset || 0;
      rafId = requestAnimationFrame(loop);
    }

    function stopLoop() {
      if (rafId === null) return;
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    function onVisibilityChange(entries) {
      var entry = entries[0];
      visible = !!entry && entry.isIntersecting;

      if (visible) {
        /* Defensive: if the eager init below failed to create an
           animation (e.g. trackWidth was zero for the entire 2s retry
           budget because the section was display:none at boot), give
           it one more shot now that the user has actually reached the
           section — layout is guaranteed to be finalized by this
           point. */
        ensureAnimation();
        if (animation && animation.playState === 'paused') {
          animation.play();
        }
        /* Desktop + opt-in: spin up the parallax loop. Mobile always
           skips it, and the diagnostic gate above keeps it off by
           default on desktop too unless `?marqueeParallax=1` is in
           the URL. With parallax disabled, the WAAPI animation just
           runs at playbackRate=1 — same on-screen behavior as a pure
           CSS @keyframes loop, with zero per-frame main-thread cost. */
        if (!isMobileViewport && parallaxEnabled) {
          startLoop();
        }
      } else {
        if (!isMobileViewport && parallaxEnabled) {
          stopLoop();
        }
        /* Pause (don't cancel) so currentTime is preserved — resume
           is instant when the user scrolls back into view. */
        if (animation && animation.playState === 'running') {
          animation.pause();
        }
        /* Bleed the scroll boost back toward 0 while paused so the
           animation doesn't resume mid-decay with stale velocity.
           No-op when parallax is disabled or on mobile (boost is
           never accumulated) but harmless. */
        scrollBoost = 0;
      }
    }

    /* CRITICAL: create the animation eagerly at init, BEFORE IO is
       set up. Previously animation creation lived inside the IO
       callback, which meant it ran exactly once per intersection
       transition. If that single fire happened at a moment where
       layout wasn't fully settled (sub-pixel zero scrollWidth, flex
       constraint mid-resolve, font swap mid-flight) ensureAnimation
       returned without creating an animation and IO would never
       refire to retry — stranding the marquee static until the user
       scrolled out and back in.

       Doing it eagerly here:
         (a) ensureAnimation has the full 120-frame rAF retry budget
             starting from `init()` time, which is basically as early
             as it can possibly run. By the time the section is in
             view layout has had several seconds to settle.
         (b) The animation auto-plays in 'running' state, but IO
             below will immediately pause it again if the section is
             offscreen at observe time — so no compositor cycles are
             wasted on something the user can't see. The eager init
             is purely about getting the Animation OBJECT created
             early; the play/pause control still belongs to IO. */
    ensureAnimation();

    if (typeof IntersectionObserver === 'function') {
      /* Generous rootMargin so the animation is fully initialized and
         playing by the time the marquee actually crosses into view.
         100px was tight enough that on a fast scroll-down the user
         could perceive a frozen bar for one or two frames before IO
         fired and ensureAnimation() ran. 400px gives the section
         roughly 6–7 frames of head start at typical scroll velocities,
         which is well below any battery cost (the WAAPI animation is
         compositor-driven and nearly free until visible) but above the
         human-detectable freeze threshold. */
      var io = new IntersectionObserver(onVisibilityChange, {
        rootMargin: '400px 0px'
      });
      io.observe(marquee);
    } else {
      /* No IntersectionObserver — assume always visible. This is the
         legacy-browser fallback; modern mobile browsers all support
         IntersectionObserver, so this branch effectively never
         runs in production but keeps the script defensive. */
      visible = true;
      if (!isMobileViewport && parallaxEnabled) {
        startLoop();
      }
    }
  }

  function initAll() {
    var marquees = document.querySelectorAll('.dd-marquee');
    for (var i = 0; i < marquees.length; i++) {
      initMarquee(marquees[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-run init when the section is re-rendered via Shopify's theme
     editor live preview or the Section Rendering API. New `.dd-marquee`
     nodes get picked up; already-initialised tracks are guarded by the
     data-dd-marquee-init flag so they don't double-bind. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target && e.target.querySelector && e.target.querySelector('.dd-marquee')) {
      initAll();
    }
  });
})();
